import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { UiIcons } from '@pipebase/ui';
import type { FavoriteKind } from '../hooks/useFavorites';
import { useRecent } from '../hooks/useRecent';

/** Route the recent item to the same href as a favorite of the same kind. */
function recentHref(kind: FavoriteKind, id: string): string {
  switch (kind) {
    case 'flow':
      return `/edit-next/${encodeURIComponent(id)}`;
    case 'event-design':
      return `/event-design/${encodeURIComponent(id)}`;
    case 'value-mapping':
      return `/value-mappings/${encodeURIComponent(id)}`;
    case 'message-mapping':
      return `/mappings/${encodeURIComponent(id)}`;
    case 'connection':
      return `/connections#${encodeURIComponent(id)}`;
    case 'runtime':
      return `/runtimes/${encodeURIComponent(id)}`;
    case 'parameter':
      return `/parameters#${encodeURIComponent(id)}`;
  }
}

const KIND_LABEL: Record<FavoriteKind, string> = {
  flow: 'F',
  'event-design': 'E',
  'value-mapping': 'V',
  'message-mapping': 'M',
  connection: 'C',
  runtime: 'R',
  parameter: 'P',
};

function kindGlyph(kind: FavoriteKind) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 16,
        height: 16,
        fontSize: 10,
        fontWeight: 700,
        color: 'var(--text-3)',
        background: 'var(--bg-surface-2)',
        borderRadius: 4,
      }}
    >
      {KIND_LABEL[kind]}
    </span>
  );
}

/**
 * Recents section rendered inside the sidebar scroll area.
 * Extracted from Sidebar.tsx so the package Sidebar can accept it as a
 * `recents` ReactNode slot — keeping the SPA-specific hook dependencies
 * out of @pipebase/ui.
 */
export function RecentsSection() {
  const [recentOpen, setRecentOpen] = useState(true);
  const { items: recent } = useRecent();

  if (recent.length === 0) return null;

  return (
    <div className={`sidebar__group ${!recentOpen ? 'is-collapsed' : ''}`}>
      <button
        type="button"
        className="sidebar__group-header"
        onClick={() => setRecentOpen((o) => !o)}
        data-testid="sidebar-recent-header"
      >
        <span className="sidebar__group-chevron">{UiIcons.chevronDown}</span>
        <span>Recent</span>
      </button>
      <div className="sidebar__group-list">
        {recent.map((r) => (
          <NavLink
            key={`recent-${r.kind}-${r.id}`}
            to={recentHref(r.kind, r.id)}
            className="nav-item"
            data-testid="sidebar-recent-item"
            title={r.title}
          >
            <span className="nav-item__icon">{kindGlyph(r.kind)}</span>
            <span className="nav-item__label">{r.title}</span>
          </NavLink>
        ))}
      </div>
    </div>
  );
}
